import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Calendar, MapPin, Users, XCircle, Trash2, Sparkles } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";
import { fmtTimePT, fmtFullDatePT } from "../../lib/time";
import { resolveImageUrl } from "../../lib/media";
import { useUrlState } from "../../lib/useUrlState";

export default function AdminEvents() {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [series, setSeries] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  // ?when=past&vis=members_only is shareable; the defaults stay out of the URL
  // (see lib/useUrlState).
  const [when, setWhen] = useUrlState("when", "upcoming", { push: true });
  const [vis, setVis] = useUrlState("vis", "any", { push: true });

  // The admin response is the FULL history — the server only date-filters for
  // non-admins — so this splits client-side with no extra request. An event
  // with no date is treated as upcoming, matching the public grid.
  const ended = (e) => {
    const ref = e.end_at || e.start_at;
    return ref ? new Date(ref) < new Date() : false;
  };
  // Time first, then visibility, so the visibility counts describe the window
  // you are actually looking at rather than the whole table.
  const inWindow = useMemo(() => (
    when === "all" ? [...events] : events.filter((e) => (when === "past" ? ended(e) : !ended(e)))
  ), [events, when]);

  const shownEvents = useMemo(() => {
    const list = vis === "any" ? [...inWindow] : inWindow.filter((e) => e.visibility === vis);
    // An archive reads backwards; a schedule reads forwards.
    return list.sort((a, b) => (when === "past"
      ? String(b.start_at || "").localeCompare(String(a.start_at || ""))
      : String(a.start_at || "").localeCompare(String(b.start_at || ""))));
  }, [inWindow, vis, when]);
  const counts = useMemo(() => ({
    upcoming: events.filter((e) => !ended(e)).length,
    past: events.filter(ended).length,
    all: events.length,
  }), [events]);

  // `internal` is what a Luma-private event maps to, so this doubles as "show me
  // everything the public can never see" — the fastest audit of what an import
  // actually brought in.
  const visCounts = useMemo(() => ({
    any: inWindow.length,
    public: inWindow.filter((e) => e.visibility === "public").length,
    members_only: inWindow.filter((e) => e.visibility === "members_only").length,
    internal: inWindow.filter((e) => e.visibility === "internal").length,
  }), [inWindow]);

  const load = () => {
    // when=all explicitly: the server now applies the time window to every
    // caller, admin included, so the admin grid must ask for the full history
    // rather than relying on being exempt from the filter.
    api.get("/events", { params: { when: "all" } }).then((r) => setEvents(r.data));
    api.get("/sessions/series").then((r) => setSeries(r.data)).catch(() => setSeries([]));
  };
  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      if (confirmDel.kind === "event") {
        await api.delete(`/events/${confirmDel.id}`);
      } else {
        await api.delete(`/sessions/series/${confirmDel.id}`);
      }
      toast.success("Deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AdminShell
      testId="admin-events-page"
      eyebrow="Events"
      title="Manage events"
      subtitle="General events and 1:1 sessions in one place."
      actions={(
        <>
          <Link to="/admin/sessions" className="btn-ghost text-sm" data-testid="admin-capital-link">Requests</Link>
          <Link to="/admin/events/new" className="btn-primary" data-testid="create-event-btn"><Plus size={14} /> New event</Link>
        </>
      )}
    >
          {/* Two independent dimensions, deliberately two controls: WHEN it
              happens and WHO may see it. One combined row would imply they are
              alternatives. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6" data-testid="admin-event-filters">
            <div className="segmented" data-testid="admin-when-filter">
              {[{ k: "upcoming", label: "Upcoming" }, { k: "past", label: "Past" }, { k: "all", label: "All" }].map((o) => (
                <button key={o.k} onClick={() => setWhen(o.k)} data-testid={`admin-when-${o.k}`}
                        className={`segmented-btn text-sm ${when === o.k ? "active" : ""}`}>
                  {o.label} <span className="opacity-60">{counts[o.k]}</span>
                </button>
              ))}
            </div>
            <div className="segmented" data-testid="admin-vis-filter">
              {[{ k: "any", label: "Any" }, { k: "public", label: "Public" },
                { k: "members_only", label: "Members" }, { k: "internal", label: "Internal" }].map((o) => (
                <button key={o.k} onClick={() => setVis(o.k)} data-testid={`admin-vis-${o.k}`}
                        className={`segmented-btn text-sm ${vis === o.k ? "active" : ""}`}
                        title={o.k === "internal" ? "Never shown publicly — Luma-private events map here" : undefined}>
                  {o.label} <span className="opacity-60">{visCounts[o.k]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {shownEvents.length === 0 && series.length === 0 && (
              <div className="col-span-full glass-panel p-12 text-center text-muted-foreground">
                {events.length === 0
                  ? "No events yet. Click \u201cNew event\u201d to compose your first."
                  : `No ${vis === "any" ? "" : vis === "members_only" ? "members-only " : vis + " "}${when === "past" ? "past " : when === "upcoming" ? "upcoming " : ""}events.`}
              </div>
            )}

            {shownEvents.map((e) => (
              <div key={e.id} className="glass-panel overflow-hidden hover-lift relative group" data-testid={`admin-event-${e.id}`}>
                <Link to={`/admin/events/${e.id}/manage`} className="block">
                  {/* Square for Luma (their covers are 1200x1200); wide for
                      native, which are authored for it. */}
                  <div className={`overflow-hidden relative ${e.source === "luma" ? "aspect-square" : "aspect-[16/9]"}`}>
                    {e.cover_image ? <img src={resolveImageUrl(e.cover_image)} alt={e.title} className="h-full w-full object-cover" /> : <div className="aurora-cover w-full h-full" />}
                    {e.cancelled && (<div className="absolute top-3 left-3 chip chip-coral"><XCircle size={11} />Cancelled</div>)}
                  </div>
                  <div className="p-5">
                    <div className="flex gap-1.5 flex-wrap">
                      {e.format === "slots" && <span className="chip chip-blue">1:1 Session</span>}
                      {e.source === "luma" && <span className="chip chip-purple">Luma</span>}
                      <span className={`chip ${e.visibility === "public" ? "chip-peach" : e.visibility === "members_only" ? "chip-purple" : "chip-magenta"}`}>{e.visibility}</span>
                      {e.require_approval && <span className="chip chip-cream">Approval</span>}
                      {e.waitlist_enabled && <span className="chip chip-green">Waitlist</span>}
                    </div>
                    <h3 className="mt-3 font-heading text-lg leading-snug">{e.title}</h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground font-mono uppercase">
                      <span className="flex items-center gap-1"><Calendar size={11} /> {fmtFullDatePT(e.start_at)} · {fmtTimePT(e.start_at)}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground font-mono">
                      <span className="flex items-center gap-1"><MapPin size={11} /> {e.campus_id === "cmp_pa" ? "Palo Alto" : e.campus_id === "cmp_mp" ? "Milpitas" : e.custom_venue || "TBD"}</span>
                      <span className="flex items-center gap-1"><Users size={11} /> {e.registration_count || 0}</span>
                    </div>
                  </div>
                </Link>
                {e.source === "luma" && (
                  <button
                    onClick={async (ev) => {
                      ev.stopPropagation(); ev.preventDefault();
                      try {
                        await api.post(`/events/${e.id}/public-on-grid`, { on: !e.public_on_grid });
                        toast.success(e.public_on_grid ? "Hidden from the public grid" : "Shown on the public grid");
                        load();
                      } catch { toast.error("Failed"); }
                    }}
                    className="absolute bottom-3 right-3 chip text-[10px] cursor-pointer"
                    style={e.public_on_grid ? { background: "hsl(var(--vd-green))", color: "white" } : {}}
                    data-testid={`luma-grid-toggle-${e.id}`}
                    title="Show or hide this Luma event on the public events page"
                  >
                    {e.public_on_grid ? "On public grid" : "Hidden from grid"}
                  </button>
                )}
                {e.source === "luma" && (
                  <button
                    onClick={async (ev) => {
                      ev.stopPropagation(); ev.preventDefault();
                      const next = e.rsvp_mode === "native" ? "outlink" : "native";
                      try {
                        await api.post(`/events/${e.id}/rsvp-mode`, { mode: next });
                        toast.success(next === "native" ? "Native RSVP: registrations happen here and write back to Luma" : "Outlink RSVP: the card links out to Luma");
                        load();
                      } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                    }}
                    className="absolute bottom-3 left-3 chip text-[10px] cursor-pointer"
                    style={e.rsvp_mode_effective === "native" ? { background: "rgba(175,138,216,0.92)", color: "white" } : {}}
                    data-testid={`luma-rsvp-mode-toggle-${e.id}`}
                    title="Native: RSVP on our site, pushed to Luma. Outlink: the card links out to lu.ma. Paid/approval Luma events always outlink."
                  >
                    {e.rsvp_mode_effective === "native" ? "Native RSVP" : e.rsvp_mode === "native" ? "Outlink (forced)" : "Outlink RSVP"}
                  </button>
                )}
                <button
                  onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); setConfirmDel({ kind: "event", id: e.id, title: e.title }); }}
                  className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/85 backdrop-blur border border-white/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:text-destructive"
                  data-testid={`delete-event-${e.id}`}
                  aria-label="Delete event"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            {/* Legacy series cards: shown ONLY for series with no shadow event yet
                (unmigrated). A migrated 1:1 session renders as an event card above
                (→ the unified manage page with the Slots tab), so listing the
                series too produced a confusing duplicate leading to the old
                editor. */}
            {/* 1:1 session series are standing programs, not dated occurrences,
                so they have no place in the Past archive — they'd otherwise sit
                there implying they had ended. Upcoming and All still list them. */}
            {/* Series are native, members-facing programmes: they belong under
                Any and Members, not Public or Internal. */}
            {(when === "past" || (vis !== "any" && vis !== "members_only")
              ? []
              : series.filter((s) => !events.some((e) => e.id === s.id))).map((s) => (
              <div key={s.id} className="glass-panel overflow-hidden hover-lift relative group" data-testid={`admin-series-${s.id}`}>
                <button onClick={() => nav(`/admin/series/${s.id}/edit`)} className="block w-full text-left">
                  {/* Series cards are native artwork; wide box is correct here. */}
                  <div className="aspect-[16/9] overflow-hidden relative bg-neutral-100">
                    <img src={resolveImageUrl(s.investor_photo)} alt={s.investor_name} className="h-full w-full object-cover object-top" />
                    <div className="absolute top-3 left-3 chip chip-purple"><Sparkles size={11} />Founder Labs</div>
                  </div>
                  <div className="p-5">
                    <div className="flex gap-1.5 flex-wrap">
                      <span className="chip chip-purple">members-only</span>
                      <span className="chip chip-cream">{s.booking_mode === "approval" ? "Approval" : "First-come"}</span>
                    </div>
                    <h3 className="mt-3 font-heading text-lg leading-snug">{s.title || `With ${s.investor_name}`}</h3>
                    <div className="mt-2 text-xs text-muted-foreground font-mono">{s.investor_name} · {s.investor_firm}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground font-mono">
                      <span className="flex items-center gap-1"><MapPin size={11} /> {s.campus_id === "cmp_pa" ? "Palo Alto" : "Milpitas"}</span>
                      <span className="flex items-center gap-1"><Calendar size={11} /> {s.day_of_week} · {s.start_hour}:00–{s.end_hour}:00 PT</span>
                    </div>
                  </div>
                </button>
                <button
                  onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); setConfirmDel({ kind: "series", id: s.id, title: s.title || `With ${s.investor_name}` }); }}
                  className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/85 backdrop-blur border border-white/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:text-destructive"
                  data-testid={`delete-series-${s.id}`}
                  aria-label="Delete series"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>

        {confirmDel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(20,22,28,0.35)" }} onClick={() => !busy && setConfirmDel(null)}>
            <div className="glass-panel p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
              <div className="h-10 w-10 rounded-full flex items-center justify-center mb-3" style={{ background: "hsl(var(--vd-coral) / 0.14)", color: "hsl(var(--destructive))" }}>
                <Trash2 size={16} />
              </div>
              <Overline>{confirmDel.kind === "series" ? "Delete Founder Labs?" : "Delete event?"}</Overline>
              <h3 className="font-heading text-lg mt-2 leading-snug">{confirmDel.title}</h3>
              <p className="text-sm text-muted-foreground mt-3">
                This will permanently remove the {confirmDel.kind === "series" ? "session series and all its slots" : "event and all its registrations"}. This cannot be undone.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmDel(null)} disabled={busy} className="btn-ghost text-sm" data-testid="delete-cancel">Cancel</button>
                <button onClick={doDelete} disabled={busy} className="btn-primary text-sm" style={{ background: "hsl(var(--destructive))" }} data-testid="delete-confirm-btn">
                  {busy ? "Deleting…" : "Delete permanently"}
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminShell>
  );
}
